// ============================================================================
// GESTIMMONY — Visite guidée interactive (product tour)
// ============================================================================
// Parcours pas-à-pas qui met en évidence les fonctionnalités clés lors de la
// première connexion d'un nouveau bailleur. Basé sur driver.js.
// - Démarrage auto une seule fois par utilisateur (flag localStorage).
// - Bouton « Visite guidée » sur le tableau de bord pour la rejouer.
// Les étapes ciblant un élément absent du DOM (ex. sidebar masquée sur mobile)
// sont automatiquement ignorées.
// ============================================================================
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

export const tourKey = (user) => `gestimmony_tour_seen_${user?.id || "anon"}`;

const RAW_STEPS = [
  {
    popover: {
      title: "Bienvenue sur Gestimmony 👋",
      description:
        "Faisons un tour rapide (moins d'une minute) des fonctionnalités clés pour bien démarrer la gestion de vos biens.",
    },
  },
  {
    element: "[data-testid='kpi-portfolio']",
    popover: {
      title: "Vos indicateurs clés",
      description:
        "Parc, taux d'occupation, encaissé du mois et impayés : votre activité en un coup d'œil, mise à jour automatiquement.",
    },
  },
  {
    element: "[data-testid='nav-properties']",
    popover: {
      title: "1. Vos biens",
      description: "Commencez ici : ajoutez vos logements (adresse, DPE, surface, loyer…).",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-tenants']",
    popover: {
      title: "2. Vos locataires",
      description: "Enregistrez vos locataires et leurs documents, puis invitez-les sur leur espace dédié.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-leases']",
    popover: {
      title: "3. Vos baux",
      description: "Créez des baux conformes (vide, meublé, commercial) et faites-les signer électroniquement.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-receipts']",
    popover: {
      title: "4. Quittances",
      description: "Générez vos quittances PDF en un clic et déposez-les dans l'espace du locataire.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-finances']",
    popover: {
      title: "Finances & rentabilité",
      description: "Suivez vos revenus, votre rentabilité par bien et préparez votre déclaration 2044.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-alerts']",
    popover: {
      title: "Alertes intelligentes",
      description: "Gestimmony vous prévient : baux à échéance, impayés, DPE périmé, révision IRL…",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-assistant']",
    popover: {
      title: "Assistant IA",
      description: "Posez vos questions (fiscalité, courriers, calculs) : votre copilote vous répond 24h/24.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-testid='nav-resources']",
    popover: {
      title: "Ressources & guides",
      description: "Des guides clairs sur la révision IRL, la 2044, les charges… pour décider sans vous tromper.",
      side: "right",
      align: "start",
    },
  },
  {
    popover: {
      title: "C'est parti ! 🚀",
      description:
        "Vous êtes prêt. Commencez par ajouter votre premier bien — tout le reste s'enchaîne naturellement.",
    },
  },
];

function availableSteps() {
  return RAW_STEPS.filter((s) => !s.element || document.querySelector(s.element));
}

export function startProductTour() {
  const instance = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: "rgba(15, 23, 42, 0.72)",
    stagePadding: 6,
    stageRadius: 10,
    nextBtnText: "Suivant",
    prevBtnText: "Précédent",
    doneBtnText: "Terminer",
    progressText: "{{current}} / {{total}}",
    steps: availableSteps(),
  });
  instance.drive();
  return instance;
}

// Démarre automatiquement la visite une seule fois par utilisateur.
export function maybeAutoStartTour(user) {
  if (!user) return;
  const key = tourKey(user);
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, "1");
  setTimeout(() => startProductTour(), 800);
}
